/* eslint-disable no-nested-ternary */
import CareAdvisorProfileCardModal from '@brands/Dashboard/ProfileCards/CareAdvisor/ProfileCards';
import CollectorProfileCardModal from '@brands/Dashboard/ProfileCards/Collector/ProfileCards';
import ProviderProfileCardModal from '@brands/Dashboard/ProfileCards/Provider/ProfileCards';
import PatientProfileCardModal from '@brands/Dashboard/ProfileCards/Patient/ProfileCards';
import { selectAuth } from '@brands/store/selectors/auth';
import { ColumnVisibilityKey } from '@brands/store/slices/columnVisibilitySlice';
import { setIsAdminLoginNewDesign } from '@brands/store/slices/isAdminNewDesignSlice';
import { clearPageState } from '@brands/store/slices/pageStateSlice';
import {
  resetForm,
  resetIsFromSmileScanReport,
  resetSelectedPatient,
  setSelectedPatient,
} from '@brands/store/slices/patientFormSlice';
import { TablePaginationKey } from '@brands/store/slices/tablePaginationSlice';
import {
  adminTypeOptions,
  careTeamTypeOptions,
  patientTypeOptions,
  stateOptions,
  userStatusOptions,
} from '@brands/Utils/selectOptions';
import {
  getSessionArray,
  getSessionNumber,
  getSessionString,
  readSessionTableState,
  SessionTableState,
  writeSessionTableState,
} from '@brands/Utils/sessionTableState';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, TextField } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus } from 'react-icons/fi';

import useDebounce from '../../../hooks/useDebounce';
import { usePersistedRowsPerPage } from '../../../hooks/usePersistedRowsPerPage';
import { useAppDispatch, useAppSelector } from '../../../hooks/useReduxHook';
import useUsers from '../../../hooks/useUsers';
import { getUserAvatar } from '../../../services/identity/getUserAvatar';
import { listAllClientTags } from '../../../services/identity/listAllClientTags';
import { getClientDivisionOptions } from '../../../services/identity/clientDivisionOptions';
import { listAllOrganizations } from '../../../services/identity/listAllOrganizations';
import { IListUsersParams } from '../../../services/identity/listAllUsers';
import { listNetworkOptions, NetworkOption } from '../../../services/identity/networkOptions';
import { ClientTag, Organization, UserProfile, UserRoleName } from '../../../services/identity/types/UserProfile';
import { displayErrorDetails } from '../../../Utils/displayError';
import CADataGrid, { caGridStyles, ColumnDef } from '../CareAdvisor/Components/CADataGrid';
import styles from './styles.module.scss';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';

interface Option {
  label: string;
  action: () => void;
}

type OptionType = {
  value: string;
  label: string;
  id?: string;
};

const filterLabelSx = { fontSize: 12, fontWeight: 400 };

type SortField =
  | 'full_name'
  | 'email'
  | 'external_id'
  | 'id'
  | 'status'
  | 'type'
  | 'organization_ids'
  | 'client_tags'
  | 'state'
  | 'network';

const SORT_KEY_MAP: Partial<Record<string, SortField>> = {
  full_name: 'full_name',
  email: 'email',
  external_id: 'external_id',
  id: 'id',
  organization_ids: 'organization_ids',
  client_tags: 'client_tags',
  status: 'status',
  type: 'type',
  state: 'state',
  network: 'network',
};

type UserTab = 1 | 2 | 3 | 4;
type UserFilterValues = Record<string, string | string[] | undefined>;

const TAB_LABELS: Record<UserTab, string> = {
  1: 'Patients',
  2: 'Providers',
  3: 'Care Team',
  4: 'Admins',
};

const USER_TAB_QUERY_KEY = 'user_tab';

const TAB_QUERY_VALUES: Record<UserTab, string> = {
  1: 'patients',
  2: 'providers',
  3: 'care-team',
  4: 'admins',
};

const QUERY_VALUE_TO_TAB = Object.entries(TAB_QUERY_VALUES).reduce<Record<string, UserTab>>((acc, [tab, value]) => {
  acc[value] = Number(tab) as UserTab;
  return acc;
}, {});

const SESSION_STATE_KEY = 'dentistry-one:admin:users';

const USER_TABS: UserTab[] = [1, 2, 3, 4];

// Only Providers and Care Team members carry a network.
const NETWORK_TABS: UserTab[] = [2, 3];

const SHARED_USER_FILTER_KEYS = [
  'full_name',
  'email',
  'external_id',
  'id',
  'state',
  'status',
  'type',
  'organization_ids',
  'client_tags',
  'network',
] as const;

const USER_TAB_STATE_KEYS = ['page', 'sort_by', 'sort_direction'] as const;

const getUserTabStateKey = (tab: UserTab, key: string): string => `${TAB_QUERY_VALUES[tab]}:${key}`;

const getScopedSessionString = (
  state: SessionTableState,
  tab: UserTab,
  key: string,
  fallback = '',
  includeLegacy = false,
): string =>
  getSessionString(
    state,
    getUserTabStateKey(tab, key),
    includeLegacy ? getSessionString(state, key, fallback) : fallback,
  );

const getSharedSessionString = (
  state: SessionTableState,
  tab: UserTab,
  key: string,
  fallback = '',
  includeLegacy = false,
): string =>
  getSessionString(
    state,
    key,
    includeLegacy ? getSessionString(state, getUserTabStateKey(tab, key), fallback) : fallback,
  );

const getSharedSessionArray = (
  state: SessionTableState,
  tab: UserTab,
  key: string,
  includeLegacy = false,
): string[] => {
  const shared = getSessionArray(state, key);
  if (shared.length > 0 || !includeLegacy) return shared;
  return getSessionArray(state, getUserTabStateKey(tab, key));
};

const getScopedSessionNumber = (
  state: SessionTableState,
  tab: UserTab,
  key: string,
  fallback: number,
  includeLegacy = false,
): number =>
  getSessionNumber(
    state,
    getUserTabStateKey(tab, key),
    includeLegacy ? getSessionNumber(state, key, fallback) : fallback,
  );

const readUserTabState = (
  state: SessionTableState,
  tab: UserTab,
  includeLegacy = false,
): {
  filters: UserFilterValues;
  page: number;
  sortBy: string;
  sortDirection: 'asc' | 'desc';
} => {
  const sortBy = getScopedSessionString(state, tab, 'sort_by', 'full_name', includeLegacy);
  const sortDirection = getScopedSessionString(state, tab, 'sort_direction', 'asc', includeLegacy);

  return {
    filters: {
      full_name: getSharedSessionString(state, tab, 'full_name', '', includeLegacy),
      email: getSharedSessionString(state, tab, 'email', '', includeLegacy),
      external_id: getSharedSessionString(state, tab, 'external_id', '', includeLegacy),
      id: getSharedSessionString(state, tab, 'id', '', includeLegacy),
      state: getSharedSessionString(state, tab, 'state', '', includeLegacy),
      status: getSharedSessionArray(state, tab, 'status', includeLegacy),
      type: getSharedSessionArray(state, tab, 'type', includeLegacy),
      organization_ids: getSharedSessionArray(state, tab, 'organization_ids', includeLegacy),
      client_tags: getSharedSessionArray(state, tab, 'client_tags', includeLegacy),
      network: getSharedSessionArray(state, tab, 'network', includeLegacy),
    },
    page: getScopedSessionNumber(state, tab, 'page', 0, includeLegacy),
    sortBy: SORT_KEY_MAP[sortBy] ? sortBy : 'full_name',
    sortDirection: sortDirection === 'desc' ? 'desc' : 'asc',
  };
};

export type UserSearchProps = {
  requestInlineAddUser?: (roleQuery: string | null) => void;
};

const UserSearch = ({ requestInlineAddUser }: UserSearchProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const roleNavigate = useRoleNavigate();
  const savedState = useMemo(() => readSessionTableState(SESSION_STATE_KEY), []);
  const { userInfo } = useAppSelector(selectAuth);
  const initialActiveTab = useMemo((): UserTab => {
    const tabValue = getSessionString(savedState, USER_TAB_QUERY_KEY);
    return QUERY_VALUE_TO_TAB[tabValue] ?? 1;
  }, [savedState]);
  const initialTabState = useMemo(
    () => readUserTabState(savedState, initialActiveTab, true),
    [initialActiveTab, savedState],
  );

  const goToAddUser = (roleQuery: string | null): void => {
    if (requestInlineAddUser) {
      requestInlineAddUser(roleQuery);
      return;
    }
    if (roleQuery) {
      roleNavigate(`/users/new?role=${roleQuery}`);
    } else {
      roleNavigate('/users/new');
    }
  };

  const [activeTab, setActiveTab] = useState<UserTab>(initialActiveTab);
  const [careTeamAddMenuOpen, setCareTeamAddMenuOpen] = useState(false);
  const careTeamAddDropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [profileCardModalUser, setProfileCardModalUser] = useState<UserProfile | null>(null);
  const [profileCardModal, setProfileCardModal] = useState(false);

  const [organizationOptions, setOrganizationOptions] = useState<OptionType[]>([]);
  const [clientTagOptions, setClientTagOptions] = useState<OptionType[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [networkOptions, setNetworkOptions] = useState<NetworkOption[]>([]);

  const canSeeNetwork = userInfo.role.name === UserRoleName.Admin || userInfo.role.name === UserRoleName.SuperAdmin;

  const networkLookup = useMemo(
    () =>
      networkOptions.reduce<Record<string, string>>((acc, option) => {
        acc[option.code] = option.label;
        return acc;
      }, {}),
    [networkOptions],
  );

  const clientTagLookup = useMemo(
    () =>
      clientTagOptions.reduce<Record<string, string>>((acc, option) => {
        acc[option.value] = option.label;
        return acc;
      }, {}),
    [clientTagOptions],
  );

  const [filterValues, setFilterValues] = useState<UserFilterValues>(initialTabState.filters);
  const [page, setPage] = useState(initialTabState.page);
  const [rowsPerPage, setRowsPerPage] = usePersistedRowsPerPage(TablePaginationKey.SuperAdminUserSearch, 25);
  const rowsPerPageHydratedRef = useRef(false);
  const [sortColKey, setSortColKey] = useState<string>(initialTabState.sortBy);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initialTabState.sortDirection);

  const filterRole = useMemo((): UserRoleName[] => {
    switch (activeTab) {
      case 1:
        return [UserRoleName.Patient, UserRoleName.Dependent];
      case 2:
        return [UserRoleName.Provider];
      case 3:
        return [UserRoleName.CareAdvisor, UserRoleName.Collector];
      case 4:
        return userInfo.role.name === UserRoleName.OrganizationAdmin
          ? [UserRoleName.OrganizationAdmin]
          : userInfo.role.name === UserRoleName.Admin
          ? [UserRoleName.OrganizationAdmin, UserRoleName.Admin]
          : [UserRoleName.OrganizationAdmin, UserRoleName.Admin, UserRoleName.SuperAdmin];
      default:
        return [UserRoleName.Patient, UserRoleName.Dependent];
    }
  }, [activeTab, userInfo.role.name]);

  // An Admin can't see Super Admins at all, and shouldn't be offered a Type
  // filter for their own Admin peers either — only Client Admin stays
  // selectable. Super Admin (and Organization Admin logging in, who only
  // ever sees their own type) keep the full list.
  const adminTypeFilterOptions = useMemo(
    () =>
      userInfo.role.name === UserRoleName.Admin
        ? adminTypeOptions.filter(
            (option) => option.value !== UserRoleName.Admin && option.value !== UserRoleName.SuperAdmin,
          )
        : adminTypeOptions,
    [userInfo.role.name],
  );

  const selectedClientTagIdsForDivision = useMemo(() => {
    const selectedClientTags = (filterValues.client_tags as string[] | undefined) || [];
    return allClientTags.filter((t) => selectedClientTags.includes(t.code || '')).map((t) => t.id);
  }, [allClientTags, filterValues.client_tags]);

  const divisionFilterOptions = useMemo(
    () => getClientDivisionOptions(allClientTags, selectedClientTagIdsForDivision),
    [allClientTags, selectedClientTagIdsForDivision],
  );

  const params = useMemo((): IListUsersParams => {
    const selectedTypes = filterValues.type as string[] | undefined;
    const selectedStatuses = filterValues.status as string[] | undefined;
    const selectedOrganizations = filterValues.organization_ids as string[] | undefined;
    const selectedClientTags = filterValues.client_tags as string[] | undefined;
    const selectedClientDivisions = filterValues.client_division_ids as string[] | undefined;
    const selectedNetworks = filterValues.network as string[] | undefined;
    const activeTabSelectedTypes =
      selectedTypes?.filter((type): type is UserRoleName => filterRole.includes(type as UserRoleName)) ?? [];
    return {
      full_name: (filterValues.full_name as string) || undefined,
      email: (filterValues.email as string) || undefined,
      external_id: (filterValues.external_id as string) || undefined,
      id: (filterValues.id as string) || undefined,
      status: selectedStatuses && selectedStatuses.length > 0 ? selectedStatuses : undefined,
      type: activeTabSelectedTypes.length > 0 ? activeTabSelectedTypes : filterRole,
      organization_ids: selectedOrganizations && selectedOrganizations.length > 0 ? selectedOrganizations : undefined,
      client_tags: selectedClientTags && selectedClientTags.length > 0 ? selectedClientTags : undefined,
      client_division_ids:
        selectedClientDivisions && selectedClientDivisions.length > 0 ? selectedClientDivisions.map(Number) : undefined,
      // Network only applies to Providers/Care Advisors, so the Patients and Admins tabs never filter on it.
      network:
        NETWORK_TABS.includes(activeTab) && selectedNetworks && selectedNetworks.length > 0
          ? selectedNetworks
          : undefined,
      state: (filterValues.state as string) || undefined,
      page: page + 1,
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      sort_by: (SORT_KEY_MAP[sortColKey] ?? 'full_name') as SortField,
      sort_direction: sortDirection,
    };
  }, [filterValues, filterRole, page, rowsPerPage, sortColKey, sortDirection, activeTab]);

  const debouncedParams = useDebounce(params, 900);
  const { users, totalRecords, dataLoading } = useUsers(debouncedParams);

  const buildListState = useCallback(
    (
      overrides: Partial<{
        selectedTab: UserTab;
        stateTab: UserTab;
        filters: UserFilterValues;
        page: number;
        resetAllTabPages: boolean;
        rowsPerPage: number;
        sortBy: string;
        sortDirection: 'asc' | 'desc';
      }> = {},
    ): SessionTableState => {
      const nextSelectedTab = overrides.selectedTab ?? activeTab;
      const nextStateTab = overrides.stateTab ?? activeTab;
      const nextFilters = overrides.filters ?? filterValues;
      const nextState = readSessionTableState(SESSION_STATE_KEY);
      const nextRowsPerPage = overrides.rowsPerPage ?? rowsPerPage;

      USER_TAB_STATE_KEYS.forEach((key) => {
        delete nextState[key];
        delete nextState[getUserTabStateKey(nextStateTab, key)];
      });

      SHARED_USER_FILTER_KEYS.forEach((key) => {
        USER_TABS.forEach((tab) => {
          delete nextState[getUserTabStateKey(tab, key)];
        });
        nextState[key] = nextFilters[key];
      });

      nextState[USER_TAB_QUERY_KEY] = TAB_QUERY_VALUES[nextSelectedTab];
      nextState.rowsPerPage = nextRowsPerPage;
      nextState[getUserTabStateKey(nextStateTab, 'page')] = overrides.page ?? page;
      if (overrides.resetAllTabPages) {
        USER_TABS.forEach((tab) => {
          nextState[getUserTabStateKey(tab, 'page')] = overrides.page ?? 0;
        });
      }
      nextState[getUserTabStateKey(nextStateTab, 'sort_by')] = overrides.sortBy ?? sortColKey;
      nextState[getUserTabStateKey(nextStateTab, 'sort_direction')] = overrides.sortDirection ?? sortDirection;

      return nextState;
    },
    [activeTab, filterValues, page, rowsPerPage, sortColKey, sortDirection],
  );

  const persistListState = useCallback((state: SessionTableState) => {
    localStorage.setItem('adminDashboardURL', '#users');
    writeSessionTableState(SESSION_STATE_KEY, state);
  }, []);

  const handleReset = (): void => {
    dispatch(resetForm());
    dispatch(resetSelectedPatient());
    dispatch(resetIsFromSmileScanReport());
    dispatch(clearPageState());
    dispatch(setIsAdminLoginNewDesign({ userInfo }));
  };

  useEffect(() => {
    handleReset();
  }, []);

  useEffect(() => {
    if (rowsPerPageHydratedRef.current) return;
    rowsPerPageHydratedRef.current = true;
    const nextRowsPerPage = getSessionNumber(savedState, 'rowsPerPage', rowsPerPage);
    if (nextRowsPerPage !== rowsPerPage) setRowsPerPage(nextRowsPerPage);
  }, [rowsPerPage, savedState, setRowsPerPage]);

  useEffect(() => {
    const fetchOrganizationOptions = async (): Promise<void> => {
      try {
        const { nodes: organizationNodes } = await listAllOrganizations();
        let filteredOrganizations = organizationNodes;
        if (userInfo.role.name === UserRoleName.OrganizationAdmin && userInfo.organizations?.length) {
          const allowedIds = new Set(userInfo.organizations.map((organization: Organization) => organization.id));
          filteredOrganizations = organizationNodes.filter((organization) => allowedIds.has(organization.id));
        }
        setOrganizationOptions(
          filteredOrganizations.map((organization) => ({
            value: organization.id,
            label: organization.name,
            id: organization.id.toString(),
          })),
        );
      } catch (error) {
        displayErrorDetails(error);
      }
    };

    const fetchClientTagsOptions = async (): Promise<void> => {
      try {
        const { nodes: clientTags } = await listAllClientTags();
        const withCode = clientTags.filter((clientTag) => Boolean(clientTag.code));
        setClientTagOptions(
          withCode.map((clientTag) => ({
            value: clientTag.code as string,
            label: clientTag.name,
            id: clientTag.id.toString(),
          })),
        );
        setAllClientTags(withCode);
      } catch (error) {
        displayErrorDetails(error);
      }
    };

    const fetchNetworkOptions = async (): Promise<void> => {
      if (!canSeeNetwork) return;
      try {
        const catalog = await listNetworkOptions(userInfo.role.name);
        setNetworkOptions(catalog);
      } catch (error) {
        displayErrorDetails(error);
      }
    };

    fetchOrganizationOptions();
    fetchClientTagsOptions();
    fetchNetworkOptions();
  }, [userInfo.role.name, userInfo.organizations, canSeeNetwork]);

  useEffect(() => {
    if (profileCardModalUser) setProfileCardModal(true);
    else setProfileCardModal(false);
  }, [profileCardModalUser]);

  useEffect(() => {
    if (!profileCardModal) setProfileCardModalUser(null);
  }, [profileCardModal]);

  useEffect(() => {
    if (!careTeamAddMenuOpen || activeTab !== 3) return undefined;
    const closeOnOutside = (event: MouseEvent | TouchEvent): void => {
      const root = careTeamAddDropdownRef.current;
      if (root && !root.contains(event.target as Node)) {
        setCareTeamAddMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('touchstart', closeOnOutside);
    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('touchstart', closeOnOutside);
    };
  }, [careTeamAddMenuOpen, activeTab]);

  // Close tab-specific menus when switching tabs.
  useEffect(() => {
    setIsOpen(false);
    setCareTeamAddMenuOpen(false);
  }, [activeTab]);

  const updateFilter = useCallback(
    (field: string, value: string | string[] | undefined) => {
      setFilterValues((prev) => {
        const next = { ...prev };
        if (!value || (Array.isArray(value) && value.length === 0)) {
          delete next[field];
        } else {
          next[field] = value;
        }
        persistListState(buildListState({ filters: next, page: 0, resetAllTabPages: true }));
        return next;
      });
      setPage(0);
    },
    [buildListState, persistListState],
  );

  const handleSort = useCallback(
    (key: string) => {
      if (!SORT_KEY_MAP[key]) return;
      const nextSortBy = sortColKey === key ? sortColKey : key;
      const nextSortDirection = sortColKey === key ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';
      setPage(0);
      if (sortColKey === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColKey(key);
        setSortDirection('asc');
      }
      persistListState(buildListState({ page: 0, sortBy: nextSortBy, sortDirection: nextSortDirection }));
    },
    [buildListState, persistListState, sortColKey, sortDirection],
  );

  const options: Option[] = [];
  const optionsForGeneralAdmin: Option[] = [
    { label: 'Client Admin', action: () => goToAddUser(UserRoleName.OrganizationAdmin) },
  ];

  if (userInfo.role.name === UserRoleName.Admin) {
    options.push(...optionsForGeneralAdmin);
  }
  if (userInfo.role.name === UserRoleName.SuperAdmin) {
    options.push(
      ...optionsForGeneralAdmin,
      { label: 'Admin', action: () => goToAddUser(UserRoleName.Admin) },
      { label: 'Super Admin', action: () => goToAddUser(UserRoleName.SuperAdmin) },
    );
  }

  const handleCreateUser = (): void => {
    if (activeTab === 1) {
      goToAddUser(null);
    } else if (activeTab === 2) {
      goToAddUser(UserRoleName.Provider);
    } else if (activeTab === 3) {
      setCareTeamAddMenuOpen((open) => !open);
    } else if (activeTab === 4) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (action: () => void): void => {
    action();
    setIsOpen(false);
  };

  const getCreateButtonLabel = (): string => {
    switch (activeTab) {
      case 1:
        return 'Add Patient';
      case 2:
        return 'Add Provider';
      case 3:
        return 'Add to Care Team';
      case 4:
        return 'Add Admin';
      default:
        return 'Add User';
    }
  };

  const columns: ColumnDef<UserProfile>[] = useMemo(
    () => [
      {
        key: 'full_name',
        label: 'Name',
        widthPx: 200,
        renderCell: (user) => (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
            onClick={() => {
              if (
                [
                  UserRoleName.Patient,
                  UserRoleName.Provider,
                  UserRoleName.CareAdvisor,
                  UserRoleName.Collector,
                ].includes(user.role.name as UserRoleName)
              ) {
                setProfileCardModalUser(user);
              }
            }}
          >
            <img
              alt="Avatar"
              onError={(event: any) => {
                event.target.src =
                  'https://media.istockphoto.com/id/1130884625/tr/vekt%C3%B6r/kullan%C4%B1c%C4%B1-%C3%BCyesi-vekt%C3%B6r-simgesi-ui-kullan%C4%B1c%C4%B1-arabirimi-veya-profil-face-avatar-uygulamas%C4%B1.jpg?s=612x612&w=0&k=20&c=jAf5nq1ebnZo8TJbjaB9dMHMvgff7uOk67NkF5CpgB0=';
                event.target.onError = null;
              }}
              style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }}
              className="fs-exclude"
              src={getUserAvatar(user.id)}
            />
            <span className={`fs-exclude ${caGridStyles.cellText}`}>{user.full_name}</span>
          </div>
        ),
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Name"
            value={filterValues.full_name || ''}
            onChange={(e) => updateFilter('full_name', e.target.value || undefined)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.full_name), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'external_id',
        label: 'External ID',
        widthPx: 140,
        renderCell: (user) => <span className={caGridStyles.cellText}>{user.external_id || '-'}</span>,
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="External ID"
            value={filterValues.external_id || ''}
            onChange={(e) => updateFilter('external_id', e.target.value || undefined)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.external_id), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'id',
        label: 'Internal ID',
        widthPx: 140,
        renderCell: (user) => <span className={caGridStyles.cellText}>{user.id}</span>,
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Internal ID"
            value={filterValues.id || ''}
            onChange={(e) => updateFilter('id', e.target.value || undefined)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.id), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'email',
        label: 'Email address',
        widthPx: 240,
        renderCell: (user) => (
          <span className={caGridStyles.cellText} style={{ cursor: 'pointer', textDecoration: 'underline' }}>
            {user.email}
          </span>
        ),
        filterContent: (
          <TextField
            variant="standard"
            fullWidth
            label="Email"
            value={filterValues.email || ''}
            onChange={(e) => updateFilter('email', e.target.value || undefined)}
            InputProps={{ disableUnderline: true, sx: { fontSize: 13, px: 0.5 } }}
            InputLabelProps={{ shrink: Boolean(filterValues.email), sx: filterLabelSx }}
          />
        ),
      },
      {
        key: 'state',
        label: 'State',
        widthPx: 120,
        renderCell: (user) => <span className={caGridStyles.cellText}>{user.state || '-'}</span>,
        filterContent: (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={Boolean(filterValues.state)} sx={filterLabelSx}>
              State
            </InputLabel>
            <Select
              value={(filterValues.state as string) || ''}
              onChange={(e) => updateFilter('state', (e.target.value as string) || undefined)}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {stateOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
      {
        key: 'client_tags',
        label: 'Client Tag(s)',
        widthPx: 200,
        renderCell: (user) => {
          const clientValues =
            user.clients && user.clients.length > 0
              ? user.clients
                  .map((client) => clientTagLookup[client.name] || client.name)
                  .filter((v): v is string => Boolean(v && v.trim().length > 0))
              : [];
          const display = clientValues.length > 0 ? Array.from(new Set(clientValues)).join(', ') : '-';
          return <span className={caGridStyles.cellText}>{display}</span>;
        },
        filterContent: (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.client_tags as string[]) || []).length > 0} sx={filterLabelSx}>
              Client Tags
            </InputLabel>
            <Select
              multiple
              value={(filterValues.client_tags as string[]) || []}
              onChange={(e) => {
                updateFilter(
                  'client_tags',
                  (e.target.value as string[]).length > 0 ? (e.target.value as string[]) : undefined,
                );
                updateFilter('client_division_ids', undefined);
              }}
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => clientTagOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {clientTagOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox size="small" checked={((filterValues.client_tags as string[]) || []).includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
      {
        key: 'client_divisions',
        label: 'Division(s)',
        widthPx: 160,
        renderCell: (user) => (
          <span className={caGridStyles.cellText}>{(user.divisions || []).map((d) => d.name).join(', ')}</span>
        ),
        filterContent: (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.client_division_ids as string[]) || []).length > 0} sx={filterLabelSx}>
              {(filterValues.client_tags as string[])?.length ? 'Division(s)' : 'Select a client tag first'}
            </InputLabel>
            <Select
              multiple
              disabled={!(filterValues.client_tags as string[])?.length}
              value={(filterValues.client_division_ids as string[]) || []}
              onChange={(e) =>
                updateFilter(
                  'client_division_ids',
                  (e.target.value as string[]).length > 0 ? (e.target.value as string[]) : undefined,
                )
              }
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => divisionFilterOptions.find((o) => o.id === v)?.label).join(', ')
              }
            >
              {divisionFilterOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  <Checkbox
                    size="small"
                    checked={((filterValues.client_division_ids as string[]) || []).includes(o.id)}
                  />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
      // Patients and Admins have no network, so the column is hidden on those tabs.
      ...(canSeeNetwork && NETWORK_TABS.includes(activeTab)
        ? [
            {
              key: 'network',
              label: 'Network',
              widthPx: 160,
              renderCell: (user: UserProfile) => {
                const isNetworkEligible =
                  user.role.name === UserRoleName.Provider || user.role.name === UserRoleName.CareAdvisor;
                const display = isNetworkEligible && user.network ? networkLookup[user.network] || user.network : '-';
                return <span className={caGridStyles.cellText}>{display}</span>;
              },
              filterContent: (
                <FormControl variant="standard" fullWidth>
                  <InputLabel shrink={((filterValues.network as string[]) || []).length > 0} sx={filterLabelSx}>
                    Network
                  </InputLabel>
                  <Select
                    multiple
                    value={(filterValues.network as string[]) || []}
                    onChange={(e) =>
                      updateFilter(
                        'network',
                        (e.target.value as string[]).length > 0 ? (e.target.value as string[]) : undefined,
                      )
                    }
                    disableUnderline
                    sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
                    renderValue={(selected) =>
                      (selected as string[]).map((v) => networkOptions.find((o) => o.code === v)?.label).join(', ')
                    }
                  >
                    {networkOptions.map((o) => (
                      <MenuItem key={o.code} value={o.code}>
                        <Checkbox size="small" checked={((filterValues.network as string[]) || []).includes(o.code)} />
                        <ListItemText primary={o.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ),
            } as ColumnDef<UserProfile>,
          ]
        : []),
      {
        key: 'status',
        label: 'Status',
        widthPx: 130,
        renderCell: (user) => {
          const color =
            user.status === 'Deactivated' || user.status === 'Not Invited' || user.status === 'Invited'
              ? '#FA5151'
              : user.status === 'Active'
              ? '#4DB546'
              : '#FF7F00';
          const bgColor =
            user.status === 'Active'
              ? '#E8F5E9'
              : user.status === 'Deactivated' || user.status === 'Not Invited' || user.status === 'Invited'
              ? '#FFF5F5'
              : '#FFF3E0';
          return (
            <span className={caGridStyles.statusChip} style={{ color, backgroundColor: bgColor }}>
              {user.status}
            </span>
          );
        },
        filterContent: (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.status as string[]) || []).length > 0} sx={filterLabelSx}>
              Status
            </InputLabel>
            <Select
              multiple
              value={(filterValues.status as string[]) || []}
              onChange={(e) =>
                updateFilter(
                  'status',
                  (e.target.value as string[]).length > 0 ? (e.target.value as string[]) : undefined,
                )
              }
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => userStatusOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {userStatusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox size="small" checked={((filterValues.status as string[]) || []).includes(o.value)} />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
      {
        key: 'type',
        label: 'Type',
        widthPx: 140,
        renderCell: (user) => <span className={caGridStyles.cellText}>{user.role.name}</span>,
        filterContent:
          activeTab === 1 || activeTab === 3 || activeTab === 4 ? (
            <FormControl variant="standard" fullWidth>
              <InputLabel shrink={((filterValues.type as string[]) || []).length > 0} sx={filterLabelSx}>
                Type
              </InputLabel>
              <Select
                multiple
                value={(filterValues.type as string[]) || []}
                onChange={(e) =>
                  updateFilter(
                    'type',
                    (e.target.value as string[]).length > 0 ? (e.target.value as string[]) : undefined,
                  )
                }
                disableUnderline
                sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
                renderValue={(selected) => {
                  const opts =
                    activeTab === 4
                      ? adminTypeFilterOptions
                      : activeTab === 3
                      ? careTeamTypeOptions
                      : patientTypeOptions;
                  return (selected as string[]).map((v) => opts.find((o) => o.value === v)?.label).join(', ');
                }}
              >
                {(activeTab === 4
                  ? adminTypeFilterOptions
                  : activeTab === 3
                  ? careTeamTypeOptions
                  : patientTypeOptions
                ).map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    <Checkbox size="small" checked={((filterValues.type as string[]) || []).includes(o.value)} />
                    <ListItemText primary={o.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : undefined,
      },
      {
        key: 'organization_ids',
        label: 'Organization(s)',
        widthPx: 220,
        renderCell: (user) => {
          const names =
            user.organizations && user.organizations.length > 0
              ? user.organizations.map((o) => o.name).join(', ')
              : '-';
          return <span className={caGridStyles.cellText}>{names}</span>;
        },
        filterContent: (
          <FormControl variant="standard" fullWidth>
            <InputLabel shrink={((filterValues.organization_ids as string[]) || []).length > 0} sx={filterLabelSx}>
              Organization
            </InputLabel>
            <Select
              multiple
              value={(filterValues.organization_ids as string[]) || []}
              onChange={(e) =>
                updateFilter(
                  'organization_ids',
                  (e.target.value as string[]).length > 0 ? (e.target.value as string[]) : undefined,
                )
              }
              disableUnderline
              sx={{ fontSize: 13, px: 0.5, '& .MuiSelect-select': { paddingRight: '28px' } }}
              renderValue={(selected) =>
                (selected as string[]).map((v) => organizationOptions.find((o) => o.value === v)?.label).join(', ')
              }
            >
              {organizationOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  <Checkbox
                    size="small"
                    checked={((filterValues.organization_ids as string[]) || []).includes(o.value)}
                  />
                  <ListItemText primary={o.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ),
      },
    ],
    [
      filterValues,
      organizationOptions,
      clientTagOptions,
      clientTagLookup,
      divisionFilterOptions,
      networkOptions,
      networkLookup,
      canSeeNetwork,
      updateFilter,
      dispatch,
      roleNavigate,
      activeTab,
    ],
  );

  const rows = useMemo(() => users.map((user) => ({ ...user, id: user.id })), [users]);

  const availableTabs = useMemo(() => {
    const tabs: UserTab[] = [1, 2, 3];
    if (userInfo.role.name !== UserRoleName.OrganizationAdmin) {
      tabs.push(4);
    }
    return tabs;
  }, [userInfo.role.name]);

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      const nextState = buildListState({ selectedTab: 1 });
      const nextTabState = readUserTabState(nextState, 1);
      setActiveTab(1);
      setFilterValues(nextTabState.filters);
      setPage(nextTabState.page);
      setSortColKey(nextTabState.sortBy);
      setSortDirection(nextTabState.sortDirection);
      persistListState(nextState);
    }
  }, [activeTab, availableTabs, buildListState, persistListState]);

  const handleTabChange = useCallback(
    (tab: UserTab) => {
      if (tab === activeTab) return;

      const nextState = buildListState({ selectedTab: tab });
      const nextTabState = readUserTabState(nextState, tab);

      setActiveTab(tab);
      setFilterValues(nextTabState.filters);
      setPage(nextTabState.page);
      setSortColKey(nextTabState.sortBy);
      setSortDirection(nextTabState.sortDirection);
      persistListState(nextState);
    },
    [activeTab, buildListState, persistListState],
  );

  return (
    <div className={styles.casesPageContainer}>
      <h2 className={styles.sectionTitle}>Users</h2>
      <div className={styles.casesPageHeader}>
        <div className={styles.casesPageHeaderLeft}>
          <div className={styles.userTabs}>
            {availableTabs.map((tab) => (
              <button
                key={tab}
                type="button"
                className={`${styles.userTab} ${activeTab === tab ? styles.userTabActive : ''}`}
                onClick={() => handleTabChange(tab)}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>
        </div>
        <div style={{ position: 'relative' }}>
          {activeTab === 3 ? (
            <div ref={careTeamAddDropdownRef} className={styles.careTeamAddDropdownRoot}>
              <button type="button" className={styles.createCaseBtn} onClick={handleCreateUser}>
                <FiPlus size={16} />
                {getCreateButtonLabel()}
              </button>
              {careTeamAddMenuOpen && (
                <div className={styles.dropdownMenu}>
                  <div
                    onClick={() => {
                      setCareTeamAddMenuOpen(false);
                      goToAddUser(UserRoleName.CareAdvisor);
                    }}
                  >
                    <span>Care Advisor</span>
                  </div>
                  <div
                    onClick={() => {
                      setCareTeamAddMenuOpen(false);
                      goToAddUser(UserRoleName.Collector);
                    }}
                  >
                    <span>Collector</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <button type="button" className={styles.createCaseBtn} onClick={handleCreateUser}>
              <FiPlus size={16} />
              {getCreateButtonLabel()}
            </button>
          )}
          {isOpen && activeTab === 4 && (
            <div className={styles.dropdownMenu}>
              {options.map((option) => (
                <div key={option.label} onClick={() => handleOptionClick(option.action)}>
                  <span>{option.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.casesGridWrapper}>
        <CADataGrid
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          count={totalRecords}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(p) => {
            setPage(p);
            persistListState(buildListState({ page: p }));
          }}
          onRowsPerPageChange={(rpp) => {
            setRowsPerPage(rpp);
            setPage(0);
            persistListState(buildListState({ rowsPerPage: rpp, page: 0 }));
          }}
          loading={dataLoading}
          sortBy={sortColKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onRowClick={(user) => {
            dispatch(setSelectedPatient({ value: user.id.toString(), label: 'Current Patient' }));
            roleNavigate(`/review-profile/${user.role.name}/${user.id}`);
          }}
          getRowSx={(user) => (user.status === 'Deactivated' ? { backgroundColor: '#FFF5F5' } : {})}
          columnVisibilityKey={ColumnVisibilityKey.SuperAdminUserSearch}
        />
      </div>

      {profileCardModalUser?.role.name === UserRoleName.Patient && (
        <PatientProfileCardModal
          patientIdentity={profileCardModalUser}
          isOpen={profileCardModal}
          toggleModal={setProfileCardModal}
        />
      )}
      {profileCardModalUser?.role.name === UserRoleName.Provider && (
        <ProviderProfileCardModal
          isOpen={profileCardModal}
          toggleModal={setProfileCardModal}
          providerIdentity={profileCardModalUser}
        />
      )}
      {profileCardModalUser?.role.name === UserRoleName.CareAdvisor && (
        <CareAdvisorProfileCardModal
          isOpen={profileCardModal}
          toggleModal={setProfileCardModal}
          caIdentity={profileCardModalUser}
        />
      )}
      {profileCardModalUser?.role.name === UserRoleName.Collector && (
        <CollectorProfileCardModal
          isOpen={profileCardModal}
          toggleModal={setProfileCardModal}
          collectorIdentity={profileCardModalUser}
        />
      )}
    </div>
  );
};

export default UserSearch;
